import React, { useId, useState } from 'react';

import { Card, H2, Icon, Text } from '@betterment/samba-react';

import { CashAccountRow, CashAccountRowHeader } from './components/CashAccountRow';
import { InvestingAccountRow, InvestingAccountRowHeader } from './components/InvestingAccountRow';
import { UnderstandingReturnsDialog } from './components/understanding-returns/UnderstandingReturnsDialog';

import { DateRangeOption } from '../../../../graphql/schema';
import { accountsByType } from '../../../shared/utils';
import { PerformanceAccountsQuery } from '../../graphql/performanceAccounts.generated';

export type ReturnType = 'time-weighted' | 'money-weighted';

export const AccountsCard: React.FC<{
  accounts: PerformanceAccountsQuery['performance']['accounts'];
  dateRangeOption: DateRangeOption;
}> = ({ accounts, dateRangeOption }) => {
  const investmentsHeadingId = useId();
  const cashHeadingId = useId();

  const [returnType, setReturnType] = useState<ReturnType>('time-weighted');

  if (accounts.length === 0) {
    return null;
  }

  const { cashAccounts, investingAccounts } = accountsByType(accounts);

  return (
    <>
      {investingAccounts.length > 0 && (
        <Card role="region" aria-labelledby={investmentsHeadingId}>
          <Card.Header css={{ padding: 'space.300' }}>
            <H2 id={investmentsHeadingId} variant="title200">
              Investments
            </H2>
          </Card.Header>
          <Card.Content>
            <InvestingAccountRowHeader returnType={returnType} onReturnTypeChange={setReturnType} />
            {investingAccounts.map(a => (
              <InvestingAccountRow
                key={a.id}
                accountId={a.id}
                dateRangeOption={dateRangeOption}
                returnType={returnType}
              />
            ))}
          </Card.Content>
          <Card.Footer css={{ padding: 'space.300' }}>
            <UnderstandingReturnsDialog>
              <Text as="span">Understanding your returns</Text>
              <Icon name="InfoCircle" size="xsmall" />
            </UnderstandingReturnsDialog>
          </Card.Footer>
        </Card>
      )}
      {cashAccounts.length > 0 && (
        <Card role="region" aria-labelledby={cashHeadingId}>
          <Card.Header css={{ padding: 'space.300' }}>
            <H2 id={cashHeadingId} variant="title200">
              Cash
            </H2>
          </Card.Header>
          <Card.Content>
            <CashAccountRowHeader />
            {cashAccounts.map(a => (
              <CashAccountRow key={a.id} accountId={a.id} dateRangeOption={dateRangeOption} />
            ))}
          </Card.Content>
        </Card>
      )}
    </>
  );
};
